import { ChangeDetectorRef, Component, ElementRef, NgZone, OnDestroy, ViewEncapsulation } from "@angular/core";
import { Router } from "@angular/router";
import { ToastPackage, ToastrService } from "ngx-toastr";
import { Subscription } from "rxjs";

import { AccionFlotanteCc, DURACION_FLOTANTE_CC, DatosFlotanteCc, SALIDA_FLOTANTE_CC, TIPOS_FLOTANTE_CC, TipoFlotanteCc } from "./flotante-cc";

/** Entra (animación de entrada), está (corre el tiempo) o sale (animación de salida y se retira). */
type EstadoFlotante = "entra" | "activo" | "sale";

/**
 * Un mensaje flotante del módulo (lo crea ngx-toastr con toastComponent):
 * vidrio del módulo, icono y color por tipo, el texto, el enlace si lo hay,
 * botón para cerrarlo y una barra que marca el tiempo. El tiempo se detiene
 * con el cursor o el foco encima y sigue al salir; Esc lo cierra. El texto
 * llega al activarse, ya con la región viva en la página (el anfitrión la
 * lleva desde que se crea), para que los lectores de pantalla lo anuncien:
 * «status» (cortés) para éxito, información y aviso; «alert» (inmediato) solo
 * para errores.
 *
 * Sin encapsulación: el contenedor (#toast-container.cc-flotantes) lo crea
 * ngx-toastr fuera de cualquier componente. Cada regla cuelga de
 * .cc-flotantes o .cc-flotante, así que no alcanza a nada más del COS.
 */
@Component({
  selector: "app-flotante-cc",
  standalone: true,
  templateUrl: "./flotante-cc.component.html",
  styleUrls: ["./flotante-cc.component.css"],
  encapsulation: ViewEncapsulation.None,
  host: { class: "cc-flotante" },
})
export class FlotanteCcComponent implements OnDestroy {
  readonly tipo: TipoFlotanteCc;
  readonly mensaje: string;
  readonly accion: AccionFlotanteCc | null;
  readonly icono: string;
  readonly prefijo: string;
  readonly urgente: boolean;
  /** Los milisegundos que se queda (los manda el servicio: 15000). */
  readonly duracion: number;

  estado: EstadoFlotante = "entra";
  pausado = false;
  /** El texto ya está en la región viva (se pinta al activarse). */
  conTexto = false;

  private restante: number;
  private desde = 0;
  private reloj: ReturnType<typeof setTimeout> | null = null;
  private retiro: ReturnType<typeof setTimeout> | null = null;
  private readonly subs: Subscription[];

  constructor(
    private readonly paquete: ToastPackage,
    private readonly toastr: ToastrService,
    private readonly router: Router,
    private readonly cdr: ChangeDetectorRef,
    private readonly zona: NgZone,
    anfitrion: ElementRef<HTMLElement>,
  ) {
    const datos = paquete.config.payload as DatosFlotanteCc | undefined;
    this.tipo = datos?.tipo ?? "info";
    const t = TIPOS_FLOTANTE_CC[this.tipo];
    this.mensaje = paquete.message ?? "";
    this.accion = datos?.accion ?? null;
    this.icono = datos?.icono || t.icono;
    this.prefijo = t.prefijo;
    this.urgente = t.urgente;
    this.duracion = Number(paquete.config.timeOut) > 0 ? Number(paquete.config.timeOut) : DURACION_FLOTANTE_CC;
    this.restante = this.duracion;
    const region = anfitrion.nativeElement;
    region.setAttribute("role", this.urgente ? "alert" : "status");
    region.setAttribute("aria-live", this.urgente ? "assertive" : "polite");
    region.setAttribute("aria-atomic", "true");
    this.subs = [
      paquete.toastRef.afterActivate().subscribe(() => this.activa()),
      paquete.toastRef.manualClosed().subscribe(() => this.cierra()),
    ];
  }

  ngOnDestroy(): void {
    for (const s of this.subs) {
      s.unsubscribe();
    }
    this.limpiaReloj();
    if (this.retiro) {
      clearTimeout(this.retiro);
    }
  }

  /** ngx-toastr lo activa en cuanto lo monta: llega el texto y empieza a correr el tiempo. */
  activa(): void {
    if (this.estado !== "entra") {
      return;
    }
    this.estado = "activo";
    this.conTexto = true;
    this.cdr.detectChanges();
    this.arranca();
  }

  /** Con el cursor o el foco encima, el tiempo se detiene (y la barra con él). */
  pausa(): void {
    if (this.estado !== "activo" || this.pausado) {
      return;
    }
    this.pausado = true;
    this.limpiaReloj();
    this.restante = Math.max(0, this.restante - (Date.now() - this.desde));
    this.cdr.detectChanges();
  }

  reanuda(): void {
    if (this.estado !== "activo" || !this.pausado) {
      return;
    }
    this.pausado = false;
    this.cdr.detectChanges();
    this.arranca();
  }

  /** El foco salió del mensaje (no a otro botón suyo): el tiempo sigue. */
  alSalirFoco(evento: FocusEvent): void {
    const caja = evento.currentTarget as HTMLElement | null;
    const destino = evento.relatedTarget as Node | null;
    if (!caja || !destino || !caja.contains(destino)) {
      this.reanuda();
    }
  }

  /** Lo cierra con su salida y, al terminarla, ngx-toastr lo retira. */
  cierra(): void {
    if (this.estado === "sale") {
      return;
    }
    this.estado = "sale";
    this.limpiaReloj();
    this.cdr.detectChanges();
    this.zona.runOutsideAngular(() => {
      this.retiro = setTimeout(() => this.toastr.remove(this.paquete.toastId), SALIDA_FLOTANTE_CC);
    });
  }

  /** El enlace del mensaje: lleva a su ruta y el mensaje se va. */
  sigue(): void {
    if (this.accion) {
      void this.router.navigateByUrl(this.accion.ruta);
    }
    this.cierra();
  }

  private arranca(): void {
    this.desde = Date.now();
    this.zona.runOutsideAngular(() => {
      this.reloj = setTimeout(() => this.cierra(), this.restante);
    });
  }

  private limpiaReloj(): void {
    if (this.reloj) {
      clearTimeout(this.reloj);
      this.reloj = null;
    }
  }
}
